/**
 * Files attached to an online map while the server can't be reached
 * (docs/plans/2026-09-29-attachments-move-with-their-map.md, stage 2 step 4):
 * kept on this device, encrypted like any attachment, in the `waiting` store,
 * and sent when the connection returns.
 *
 * They exist nowhere else until sent, so the app never drops them to make
 * room. A file leaves the store once the server has it (it then stays as an
 * offline copy), with its map when the map is deleted, or at sign-out after
 * a warning that counts them.
 *
 * Each file's header also keeps, under `waiting`, what is needed if the
 * map's key changes before the file is sent (a member was removed): the
 * file's own key and its name, to wrap them for the new key. That exposes
 * only this file, which is on the device anyway (the plan's threat model:
 * no protection against access to the device); both go once it is sent.
 */
import {
  putStoredAttachment,
  getStoredAttachment,
  deleteStoredAttachment,
  listStoredAttachments,
  listStoredAttachmentMaps,
  deleteStoredMapAttachments,
} from '../storage/index.js';
import { buildEnvelope, parseEnvelope, uint8ToBase64, base64ToUint8 } from './attachment-envelope.js';
import { wrapBek, encryptFilename } from '../crypto/attachment-crypto.js';
import { uploadWithProgress } from '../../shared/utils/transfer-progress.js';
import { emit, Events } from '../core/events.js';

// docId → Map(blobId → bytes stored). Loaded once per session, then kept in
// step, so displaying a file asks the store only when it holds that file.
let index = new Map();
let loaded = null; // Promise, once loading started
// "docId/blobId" refused by the server this session: not tried again until
// the app restarts.
const refused = new Set();
let sending = null;

/** Read which files wait, once (at start and after signing in). */
export function loadWaitingFiles() {
  if (!loaded) {
    loaded = (async () => {
      const found = new Map();
      for (const docId of await listStoredAttachmentMaps('waiting').catch(() => [])) {
        const files = await listStoredAttachments('waiting', docId).catch(() => []);
        if (files.length) found.set(docId, new Map(files.map((file) => [file.id, file.size || 0])));
      }
      // Files kept while loading are in `index` already: keep them too.
      for (const [docId, files] of index) {
        found.set(docId, new Map([...(found.get(docId) || []), ...files]));
      }
      index = found;
      changed();
    })();
  }
  return loaded;
}

function changed() {
  emit(Events.WAITING_FILES_CHANGED, { count: waitingCount() });
}

/** How many files wait, for these maps or all of them. */
export function waitingCount(docIds = null) {
  let count = 0;
  for (const [docId, files] of index) {
    if (!docIds || docIds.includes(docId)) count += files.size;
  }
  return count;
}

/** The names of the files waiting to be sent, by map (sign-out names them). */
export async function waitingFileNames() {
  await loadWaitingFiles();
  const names = new Map();
  for (const [docId, files] of index) {
    const list = [];
    for (const blobId of files.keys()) {
      let name = '';
      try {
        const bytes = await readWaitingFile(docId, blobId);
        name = bytes ? parseEnvelope(bytes).header.waiting?.filename || '' : '';
      } catch { /* named by its ID below */ }
      list.push(name || blobId.slice(0, 8));
    }
    if (list.length) names.set(docId, list);
  }
  return names;
}

/** Files these maps still try to send this session (refused ones left out). */
export function sendableCount(docIds) {
  let count = 0;
  for (const [docId, files] of index) {
    if (!docIds.includes(docId)) continue;
    for (const blobId of files.keys()) if (!refused.has(`${docId}/${blobId}`)) count++;
  }
  return count;
}

/** Bytes this map's waiting files take on the device. */
export function waitingBytes(docId) {
  let bytes = 0;
  for (const size of index.get(docId)?.values() || []) bytes += size;
  return bytes;
}

export function isWaiting(docId, blobId) {
  return !!index.get(docId)?.has(blobId);
}

/** The file's stored bytes (the download format), or null. */
export function readWaitingFile(docId, blobId) {
  return isWaiting(docId, blobId) ? getStoredAttachment('waiting', docId, blobId) : Promise.resolve(null);
}

/**
 * Keep an encrypted file to send later. The caller checked there is room.
 * @param {{encrypted: Uint8Array, wrappedBek: Uint8Array, encryptedFilename: Uint8Array,
 *   bekRaw: Uint8Array, fingerprint: string|null, filename: string, mimeType: string, size: number}} file
 */
export async function keepWaiting(docId, blobId, file) {
  const bytes = buildEnvelope({
    wrapped_bek: uint8ToBase64(file.wrappedBek),
    filename_encrypted: uint8ToBase64(file.encryptedFilename),
    mime_type: file.mimeType,
    size_original: file.size,
    created_at: new Date().toISOString(),
    waiting: { bek: uint8ToBase64(file.bekRaw), filename: file.filename, dek_fingerprint: file.fingerprint },
  }, file.encrypted);
  await putStoredAttachment('waiting', docId, blobId, bytes);
  if (!index.has(docId)) index.set(docId, new Map());
  index.get(docId).set(blobId, bytes.length);
  changed();
}

/**
 * What the server's answer to sending a file means. Pure (tested).
 * @returns {'sent'|'stale'|'later'|'refused'} stale: wrapped for an old map
 *   key; later: try again when the connection is back; refused: the server
 *   won't take it as is (no access, quota full, ID taken): kept, the user told.
 */
export function answerMeaning(status, code = null) {
  if (status >= 200 && status < 300) return 'sent';
  if (status === 409 && code === 'dek_stale') return 'stale';
  if (status === 401 || status === 408 || status === 429 || status >= 500) return 'later';
  return 'refused';
}


/**
 * Send every waiting file the server can take now, one at a time. Stops at
 * the first sign the server can't be reached. Files of maps this account
 * can no longer open stay on the device.
 *
 * @param {{loadKey: (docId: string) => Promise<CryptoKey|null>,
 *   refreshKey: (docId: string) => Promise<CryptoKey|null>,
 *   fingerprintOf: (docId: string) => Promise<string|null>,
 *   canSend?: (docId: string) => boolean}} deps loadKey: the map's current
 *   key; refreshKey: the map's key fetched again from the server (it changed);
 *   fingerprintOf: which wrapping of the map's key this device has;
 *   canSend: whether the map is an online map this account may edit
 * @returns {Promise<{sent: number, refused: Array<{docId: string, reason: string}>}>}
 */
export function sendWaitingFiles(deps) {
  if (!sending) {
    sending = sendAll(deps).finally(() => { sending = null; });
  }
  return sending;
}

async function sendAll({ canSend = () => true, ...keys }) {
  await loadWaitingFiles();
  const result = { sent: 0, refused: [] };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return result;
  for (const [docId, files] of [...index]) {
    if (!canSend(docId)) continue;
    for (const blobId of [...files.keys()]) {
      const key = `${docId}/${blobId}`;
      if (refused.has(key)) continue;
      let outcome;
      try {
        outcome = await sendOne(docId, blobId, keys);
      } catch (error) {
        if (error?.network) return result; // still offline: try again later
        outcome = { meaning: 'refused', reason: error?.message || String(error) };
      }
      if (outcome.meaning === 'sent') {
        result.sent++;
      } else if (outcome.meaning === 'gone') {
        continue;
      } else if (outcome.meaning === 'later') {
        return result;
      } else {
        refused.add(key);
        result.refused.push({ docId, reason: outcome.reason });
        changed();
      }
    }
  }
  return result;
}

async function sendOne(docId, blobId, { loadKey, refreshKey, fingerprintOf }) {
  const bytes = await getStoredAttachment('waiting', docId, blobId);
  if (!bytes) {
    forget(docId, blobId);
    return { meaning: 'gone' };
  }
  const { header, encrypted } = parseEnvelope(bytes);
  const own = header.waiting || {};
  let wrapped = { wrapped_bek: header.wrapped_bek, filename_encrypted: header.filename_encrypted };
  let fingerprint = await fingerprintOf(docId);
  if (own.bek && fingerprint !== own.dek_fingerprint) {
    wrapped = await rewrap(await loadKey(docId), own);
  }
  let response = await post(docId, blobId, header, encrypted, wrapped, fingerprint);
  let body = response.ok ? null : await response.json().catch(() => ({}));
  if (answerMeaning(response.status, body?.code) === 'stale' && own.bek) {
    // The map's key changed on the server: take the new one and wrap again.
    const newKey = await refreshKey(docId);
    if (newKey) {
      wrapped = await rewrap(newKey, own);
      fingerprint = await fingerprintOf(docId);
      response = await post(docId, blobId, header, encrypted, wrapped, fingerprint);
      body = response.ok ? null : await response.json().catch(() => ({}));
    }
  }
  const meaning = answerMeaning(response.status, body?.code);
  if (meaning !== 'sent') return { meaning, reason: body?.error || `HTTP ${response.status}` };

  // The server has it: keep it as an offline copy (without its own key and
  // name), and only then remove it from the files waiting.
  const sentHeader = {
    ...wrapped,
    mime_type: header.mime_type,
    size_original: header.size_original,
    created_at: header.created_at,
  };
  await putStoredAttachment('offline', docId, blobId, buildEnvelope(sentHeader, encrypted)).catch(() => {});
  await deleteStoredAttachment('waiting', docId, blobId);
  forget(docId, blobId);
  return { meaning: 'sent' };
}

async function rewrap(key, own) {
  if (!key) throw new Error('No key for this map');
  return {
    wrapped_bek: uint8ToBase64(await wrapBek(key, base64ToUint8(own.bek))),
    filename_encrypted: uint8ToBase64(await encryptFilename(key, own.filename || '')),
  };
}

async function post(docId, blobId, header, encrypted, wrapped, fingerprint) {
  const form = new FormData();
  form.append('file', new Blob([encrypted]), 'blob');
  form.append('metadata', JSON.stringify({
    mime_type: header.mime_type,
    size_original: header.size_original,
    wrapped_bek: wrapped.wrapped_bek,
    filename_encrypted: wrapped.filename_encrypted,
    dek_fingerprint: fingerprint,
    blob_id: blobId,
  }));
  return uploadWithProgress(`/api/docs/${docId}/blobs`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    body: form,
  });
}

function forget(docId, blobId) {
  const files = index.get(docId);
  if (!files?.delete(blobId)) return;
  if (!files.size) index.delete(docId);
  changed();
}

/**
 * Remove the waiting files of these maps: the map was deleted, or the user
 * signed out after the warning that counts them.
 * @returns {Promise<number>} how many were removed
 */
export async function deleteWaitingFiles(docIds) {
  await loadWaitingFiles();
  let count = 0;
  for (const docId of docIds) {
    if (!index.has(docId)) continue;
    count += await deleteStoredMapAttachments('waiting', docId);
    index.delete(docId);
  }
  if (count) changed();
  return count;
}

/**
 * At sign-out, after the warning that counted them. Also removes what the
 * maps whose files were all sent left behind (an empty browser database).
 */
export async function deleteAllWaitingFiles() {
  await loadWaitingFiles();
  const count = await deleteWaitingFiles([...index.keys()]);
  const left = await listStoredAttachmentMaps('waiting', { withEmpty: true }).catch(() => []);
  for (const docId of left) await deleteStoredMapAttachments('waiting', docId).catch(() => {});
  return count;
}

/** Tests only. */
export function _resetForTests() {
  index = new Map();
  loaded = null;
  refused.clear();
  sending = null;
}
